import { $canSub } from '@pendo/permissions';
import {
    getPortals,
    createPortal,
    updatePortal,
    getPortal,
    deletePortal,
    duplicatePortal,
    getPortalIdeas,
    uploadPortalLogo,
    getPortalState,
} from '@/vuex/modules/voc-portal/voc-portal.api';
import { post as gcsPost } from '@/data/gcs-service.js';
const vocPortal = {
    namespaced: true,
    state: {
        map: {},
        error: {},
        activePortalId: null,
        logoUploading: false,
    },
    mutations: {
        setMap (state, { map }) {
            state.map = map;
        },
        setError (state, error) {
            state.error = error;
        },
        setPortal (state, { portalId, portal }) {
            if (!portalId || !portal) {
                return;
            }
            state.map[portalId] = portal;
        },
        removePortal (state, portalId) {
            delete state.map[portalId];
        },
        updatePortalIdeaPages (state, { portalId, ideaPages }) {
            const currentPortalPages = state.map[portalId].pages || [];
            const mergedPages = currentPortalPages.map((page, pageIndex) => {
                const matchedPage = ideaPages.find((ideaPage) => ideaPage.id === page.id);
                const fallbackPage = !matchedPage && !ideaPages[pageIndex]?.id ? ideaPages[pageIndex] : null;
                const fetchedPage = matchedPage || fallbackPage || {};
                const fetchedItemLists = fetchedPage.itemLists || [];
                const itemLists = (page.itemLists || []).map((itemList, itemListIndex) => {
                    const matchedItemList = fetchedItemLists.find((list) => list.id === itemList.id);
                    const fallbackItemList = !matchedItemList && !fetchedItemLists[itemListIndex]?.id
                        ? fetchedItemLists[itemListIndex]
                        : null;
                    const fetchedItemList = matchedItemList || fallbackItemList || {};

                    return {
                        ...itemList,
                        cards: fetchedItemList.cards || [],
                    };
                });

                return {
                    ...page,
                    itemLists,
                };
            });
            state.map[portalId].fetchedIdeas = true;
            state.map[portalId].pages = mergedPages;
        },
        setPortalShareDetails (state, { portalId, portalShareDetails }) {
            if (!portalId || !portalShareDetails) {
                return;
            }
            if (!state.map[portalId]) {
                state.map[portalId] = {};
            }
            state.map[portalId].shareDetails = portalShareDetails;
        },
        setActivePortalId (state, { portalId }) {
            state.activePortalId = portalId;
        },
    },
    actions: {
        async loadPortals ({ commit, state }) {
            try {
                const portals = await getPortals();
                portals.forEach((portal) => {
                    if (!state.map[portal.id]) {
                        commit('setPortal', { portalId: portal.id, portal });
                    }
                });
            } catch (error) {
                commit('setError', error);
                throw error;
            }
        },
        async loadPortalById ({ commit, state }, { portalId, refetch = false }) {
            if (refetch || !state.map[portalId]) {
                try {
                    const portal = await getPortal(portalId);
                    commit('setPortal', { portalId: portal.id, portal });
                } catch (error) {
                    commit('setError', error);
                    throw error;
                }
            }
            if (refetch || !state.map[portalId]?.fetchedIdeas) {
                try {
                    const portalIdeaConfig = await getPortalIdeas(portalId);
                    commit('updatePortalIdeaPages', { portalId, ideaPages: portalIdeaConfig });
                } catch (error) {
                    commit('setError', error);
                    throw error;
                }
            }
        },
        async createPortal ({ commit }, portal) {
            try {
                const newPortal = await createPortal(portal);
                commit('setPortal', { portalId: newPortal.id, portal: newPortal });

                return newPortal;
            } catch (error) {
                commit('setError', error);
                throw error;
            }
        },
        async updatePortal ({ commit }, { portal }) {
            try {
                const currentPages = portal.pages;
                const updatedPortal = await updatePortal({ portalId: portal.id, portal });
                commit('setPortal', { portalId: updatedPortal.id, portal: updatedPortal });
                if (currentPages !== undefined) {
                    commit('updatePortalIdeaPages', { portalId: updatedPortal.id, ideaPages: currentPages });
                }
            } catch (error) {
                commit('setError', error);
                throw error;
            }
        },
        async deletePortal ({ commit, state }, portalId) {
            if (!portalId || !state.map[portalId]) {
                return;
            }
            try {
                await deletePortal(portalId);
                commit('removePortal', portalId);
            } catch (error) {
                commit('setError', error);
                throw error;
            }
        },
        async duplicatePortal ({ commit, dispatch }, { portal }) {
            if (!portal.id) {
                return;
            }
            try {
                const duplicatedPortal = await duplicatePortal({ portalId: portal.id, portal });
                commit('setPortal', { portalId: duplicatedPortal.id, portal: duplicatedPortal });
                dispatch('loadPortals');

                return duplicatedPortal;
            } catch (error) {
                commit('setError', error);
                throw error;
            }
        },
        async uploadPortalLogo ({ commit }, { imageFile, portalId }) {
            try {
                const logoImageResp = await uploadPortalLogo(portalId);
                await gcsPost(logoImageResp.uploadUrl, imageFile, logoImageResp.formData);
                const logoUrl = `${logoImageResp.uploadUrl}/${logoImageResp.formData.key}`;

                return logoUrl;
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async getSharePortalDetails ({ commit, state }, portalId) {
            const portal = state.map[portalId];

            if (portal?.shareDetails) {
                return portal.shareDetails;
            }

            try {
                const portalShareDetails = await getPortalState(portalId);
                commit('setPortal', { portalId, portalShareDetails });

                return portalShareDetails;
            } catch (error) {
                commit('setError', error);
                throw error;
            }
        },
    },
    getters: {
        canCreatePortal: () =>
            $canSub()
                .entity('vocPortal')
                .action('create'),
        canEditPortal: () =>
            $canSub()
                .entity('vocPortal')
                .action('edit'),
        canDeletePortal: () =>
            $canSub()
                .entity('vocPortal')
                .action('remove'),
        hasPortal: (state) => Object.keys(state.map).length > 0,
        portals: (state) =>
            Object.values(state.map).sort((a, b) =>
                a.internalName.toLowerCase().localeCompare(b.internalName.toLowerCase()),
            ),
        getPortalById: (state) => (portalId) => {
            return state.map[portalId];
        },
        activePortal: (state) => {
            return state.map[state.activePortalId];
        },
        getPortalsByIds: (state) => (ids) => {
            if (!Array.isArray(ids)) return [];

            return ids
                .map((id) => state.map[id])
                .filter(Boolean)
                .map((portal) => ({
                    id: portal.id,
                    internalName: portal.internalName,
                }));
        },
    },
};

export default vocPortal;
